import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Row, Col, Divider, Card, Menu, Select } from "antd";
import { FieldTimeOutlined } from "@ant-design/icons";
import { Navigate } from "react-router-dom";
import axios from "axios";

import Transversales from "./components/Transversales";
import HistorialDrawer from "components/Drawer/HistorialDrawer";
import PerfilUsuario from "./components/PerfilUsuario";
import { PrimaryButton } from "components/common/Buttons";
import { AppContext } from "app/AppContext";
import { userHasPermission } from "utils/userHasPermission";
import {
  ENTRE_ESPECIE,
  GENERAL,
  UNIDAD_CUARENTENARIA,
} from "app_permissions/noches_vacio";

import "styles/base.scss";

const { Option } = Select;
const PROFILE_MENU_PREFIX = "profile";

const buildProfileMenuKey = (person) =>
  `${PROFILE_MENU_PREFIX}-${person.id}-${person.code}`;

const parseProfileMenuKey = (key) => {
  const stringKey = String(key || "");

  if (!stringKey.startsWith(`${PROFILE_MENU_PREFIX}-`)) return null;

  const [, id, ...codeParts] = stringKey.split("-");
  const code = codeParts.join("-");

  if (!id || !code) return null;

  return {
    id,
    code,
  };
};

const NochesVacioContainer = () => {
  const [typePerson, setTypePerson] = useState([]);
  const [typeSpecie, setTypeSpecie] = useState([]);
  const [select, setSelect] = useState("001");
  const [configuracion, setConfiguracion] = useState({});
  const [isHistorialVisible, setIsHistorialVisible] = useState(false);
  const [menuselect, setMenuselect] = useState("1");
  const [isloadingInfo, setIsLoadingInfo] = useState(true);

  const requestIdRef = useRef(0);

  const { rolData } = useContext(AppContext);

  const V_ENTRE_ESPECIE = userHasPermission(
    rolData?.role,
    ENTRE_ESPECIE.VISUALIZAR
  );
  const V_GENERAL = userHasPermission(rolData?.role, GENERAL.VISUALIZAR);
  const V_UNIDAD_CUARENTENARIA = userHasPermission(
    rolData?.role,
    UNIDAD_CUARENTENARIA.VISUALIZAR
  );

  const VER_NOCHES_VACIO =
    V_GENERAL || V_ENTRE_ESPECIE || V_UNIDAD_CUARENTENARIA;

  const isFoodPlantSpecie = select === "004";

  const typeTransversal = useMemo(
    () =>
      [
        V_ENTRE_ESPECIE && {
          id: 1,
          code: "1",
          name: "Entre especies",
        },
        V_GENERAL && {
          id: 2,
          code: "2",
          name: "General",
        },
        V_UNIDAD_CUARENTENARIA && {
          id: 3,
          code: "3",
          name: "Unidad Cuarentenaria",
        },
        {
          id: 4,
          code: "4",
          name: "Desde Planta de alimentos",
        },
        {
          id: 5,
          code: "5",
          name: "Hacia Planta de alimentos",
        },
        {
          id: 6,
          code: "6",
          name: "General",
        },
      ].filter(Boolean),
    [V_ENTRE_ESPECIE, V_GENERAL, V_UNIDAD_CUARENTENARIA]
  );

  const getAllowedMenuKeys = useCallback(() => {
    if (isFoodPlantSpecie) {
      return ["4", "5", "6"];
    }

    const allowed = [];
    if (V_ENTRE_ESPECIE) allowed.push("1");
    if (V_GENERAL) allowed.push("2");
    if (V_UNIDAD_CUARENTENARIA) allowed.push("3");

    return allowed;
  }, [isFoodPlantSpecie, V_ENTRE_ESPECIE, V_GENERAL, V_UNIDAD_CUARENTENARIA]);

  const getDefaultMenuKey = useCallback(() => {
    const allowed = getAllowedMenuKeys();
    return allowed[0] || "";
  }, [getAllowedMenuKeys]);

  const selectedProfileMenu = useMemo(
    () => parseProfileMenuKey(menuselect),
    [menuselect]
  );

  const showDrawer = () => {
    setIsHistorialVisible(true);
  };

  const onClose = () => {
    setIsHistorialVisible(false);
  };

  const titlePerson = (name) => {
    if (!name || typeof name !== "string") return "";
    return name.charAt(0) + name.slice(1).toLowerCase();
  };

  const getTypePerson = async () => {
    try {
      const url =
        process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
        "/data-master/type-personal";
      const response = await axios.get(url);
      setTypePerson(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error loading type person", error);
      setTypePerson([]);
    }
  };

  const getTypeSpecie = async () => {
    try {
      const url = process.env.REACT_APP_BASE_URL_SPECIE + "/specie";
      const response = await axios.get(url);
      const ordered = Array.isArray(response.data)
        ? [...response.data].sort((a, b) => a.id - b.id)
        : [];
      setTypeSpecie(ordered);
    } catch (error) {
      console.error("Error loading species", error);
      setTypeSpecie([]);
    }
  };

  const resetViewState = () => {
    setConfiguracion({});
    setIsLoadingInfo(true);
  };

  const handleSelect = (animal) => {
    setSelect(animal);
    resetViewState();

    if (animal === "004") {
      setMenuselect("4");
      return;
    }

    setMenuselect(
      [
        V_ENTRE_ESPECIE && "1",
        V_GENERAL && "2",
        V_UNIDAD_CUARENTENARIA && "3",
      ].filter(Boolean)[0] || ""
    );
  };

  const onClick = (e) => {
    if (!e?.key) return;
    setMenuselect(String(e.key));
    resetViewState();
  };

  useEffect(() => {
    getTypePerson();
    getTypeSpecie();
  }, []);

  useEffect(() => {
    if (selectedProfileMenu) return;

    const allowedMenus = getAllowedMenuKeys();

    if (!allowedMenus.includes(menuselect)) {
      const nextMenu = getDefaultMenuKey();
      if (nextMenu !== menuselect) {
        setMenuselect(nextMenu);
      }
    }
  }, [
    select,
    menuselect,
    selectedProfileMenu,
    getAllowedMenuKeys,
    getDefaultMenuKey,
  ]);

  const fetchDataForm = useCallback(async () => {
    if (!menuselect) {
      setConfiguracion({});
      setIsLoadingInfo(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setIsLoadingInfo(true);

    try {
      let conf = {};

      if (menuselect === "1") {
        const urlDataMaster =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/range-empty-night";
        const urlConfigSpecie =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          `/cross-specie?pyramid_specie=${select}`;
        const urlLevels = process.env.REACT_APP_BASE_URL_LEVEL + "/level";

        const [dataMasterResp, configSpecieResp, levelsResp] =
          await Promise.all([
            axios.get(urlDataMaster),
            axios.get(urlConfigSpecie),
            axios.get(urlLevels),
          ]);

        conf = {
          DataMaster: Array.isArray(dataMasterResp.data)
            ? [...dataMasterResp.data].sort((a, b) => a.id - b.id)
            : [],
          SpecieConfig: configSpecieResp.data || [],
          LevelsSpecie: levelsResp.data || [],
        };
      } else if (menuselect === "2") {
        const urlDataMaster =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/range-empty-night";
        const urlGeneralTransversal =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/general-location";
        const urlConfigGeneral =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          `/cross-general?pyramid_specie=${select}`;

        const [dataMasterResp, generalTransvResp, generalConfResp] =
          await Promise.all([
            axios.get(urlDataMaster),
            axios.get(urlGeneralTransversal),
            axios.get(urlConfigGeneral),
          ]);

        conf = {
          DataMaster: Array.isArray(dataMasterResp.data)
            ? [...dataMasterResp.data].sort((a, b) => a.id - b.id)
            : [],
          GeneralTransv: generalTransvResp.data || [],
          GeneralConf: generalConfResp.data || [],
        };
      } else if (menuselect === "3") {
        const urlDataMaster =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/range-empty-night";
        const urlTypeQuarantine =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/type-quarantine";
        const urlAlertQuarantine =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/alert-quarantine";
        const urlUnitQuarantineCross =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          `/cross-unit-quarantine?pyramid_specie=${select}`;

        const [
          dataMasterResp,
          typeQuarantineResp,
          alertQuarantineResp,
          unitQuarantineCrossResp,
        ] = await Promise.all([
          axios.get(urlDataMaster),
          axios.get(urlTypeQuarantine),
          axios.get(urlAlertQuarantine),
          axios.get(urlUnitQuarantineCross),
        ]);

        conf = {
          DataMaster: Array.isArray(dataMasterResp.data)
            ? [...dataMasterResp.data].sort((a, b) => a.id - b.id)
            : [],
          TypeQuarantine: typeQuarantineResp.data || [],
          AlertQuarantine: alertQuarantineResp.data || [],
          UnitQuarantineCross: unitQuarantineCrossResp.data || [],
        };
      } else if (menuselect === "4") {
        const urlDataMaster =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/range-empty-night";
        const urlConfigSpecie =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/cross-specie-from-plants/cross-specie?specie_id=1";
        const urlFoodPlants =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT + "/data-master/food-plants";
        const urlLevels = process.env.REACT_APP_BASE_URL_LEVEL + "/level";

        const [dataMasterResp, configSpecieResp, foodPlantsResp, levelsResp] =
          await Promise.all([
            axios.get(urlDataMaster),
            axios.get(urlConfigSpecie),
            axios.get(urlFoodPlants),
            axios.get(urlLevels),
          ]);

        conf = {
          DataMaster: Array.isArray(dataMasterResp.data)
            ? [...dataMasterResp.data].sort((a, b) => a.id - b.id)
            : [],
          SpecieConfig: configSpecieResp.data || [],
          FoodPlants: foodPlantsResp.data || [],
          LevelsSpecie: levelsResp.data || [],
        };
      } else if (menuselect === "5") {
        const urlDataMaster =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/range-empty-night";
        const urlConfigSpecie =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/cross-specie-to-plants/cross-specie?specie_id=1";
        const urlFoodPlants =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT + "/data-master/food-plants";
        const urlLevels = process.env.REACT_APP_BASE_URL_LEVEL + "/level";

        const [dataMasterResp, configSpecieResp, foodPlantsResp, levelsResp] =
          await Promise.all([
            axios.get(urlDataMaster),
            axios.get(urlConfigSpecie),
            axios.get(urlFoodPlants),
            axios.get(urlLevels),
          ]);

        conf = {
          DataMaster: Array.isArray(dataMasterResp.data)
            ? [...dataMasterResp.data].sort((a, b) => a.id - b.id)
            : [],
          SpecieConfig: configSpecieResp.data || [],
          FoodPlants: foodPlantsResp.data || [],
          LevelsSpecie: levelsResp.data || [],
        };
      } else if (menuselect === "6") {
        const urlDataMaster =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/range-empty-night";
        const urlGeneralTransversal =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/general-location";
        const urlConfigGeneral =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          `/cross-general-food-plants/list-general?code_specie=${select}`;

        const [dataMasterResp, generalTransvResp, generalConfResp] =
          await Promise.all([
            axios.get(urlDataMaster),
            axios.get(urlGeneralTransversal),
            axios.get(urlConfigGeneral),
          ]);

        conf = {
          DataMaster: Array.isArray(dataMasterResp.data)
            ? [...dataMasterResp.data].sort((a, b) => a.id - b.id)
            : [],
          GeneralTransv: generalTransvResp.data || [],
          GeneralConf: generalConfResp.data || [],
        };
      } else {
        const profileMenu = parseProfileMenuKey(menuselect);
        const typePersonalCode = profileMenu?.code || menuselect;

        const urlDataMaster =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/range-empty-night";
        const urlTypeMovement =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          "/data-master/type-movement";
        const urlTypePersonConf =
          process.env.REACT_APP_BASE_URL_EMPTY_NIGHT +
          `/movement-type-personal?pyramid_specie=${select}&type_personal=${typePersonalCode}`;

        const [dataMasterResp, typeMovementResp, typePersonConfResp] =
          await Promise.all([
            axios.get(urlDataMaster),
            axios.get(urlTypeMovement),
            axios.get(urlTypePersonConf),
          ]);

        conf = {
          DataMasterResp: Array.isArray(dataMasterResp.data)
            ? [...dataMasterResp.data].sort((a, b) => a.id - b.id)
            : [],
          typeMovementResp: typeMovementResp.data || [],
          TypePersonConfResp: typePersonConfResp.data || [],
        };
      }

      if (currentRequestId === requestIdRef.current) {
        setConfiguracion(conf);
      }
    } catch (error) {
      console.error("Error loading form configuration", error);

      if (currentRequestId === requestIdRef.current) {
        setConfiguracion({});
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoadingInfo(false);
      }
    }
  }, [menuselect, select]);

  useEffect(() => {
    fetchDataForm();
  }, [fetchDataForm]);

  const isTransversalMenu =
    !selectedProfileMenu &&
    ["1", "2", "3", "4", "5", "6"].includes(String(menuselect));
  const selectedFormulario = selectedProfileMenu?.code || menuselect;

  const childKey = `${select}-${menuselect}`;

  const component = isTransversalMenu ? (
    <Transversales
      key={childKey}
      formulario={menuselect}
      selected={select}
      species={typeSpecie}
      configuracion={configuracion}
      handleSelect={handleSelect}
      isloadingInfo={isloadingInfo}
      setconfiguracion={setConfiguracion}
    />
  ) : (
    <PerfilUsuario
      key={childKey}
      formulario={selectedFormulario}
      selected={select}
      species={typeSpecie}
      configuracion={configuracion}
      handleSelect={handleSelect}
    />
  );

  if (!VER_NOCHES_VACIO) {
    return <Navigate to="/first-login" state={{ unauthorizedError: true }} />;
  }

  return (
    <div className="contenedor_noches_vacio">
      <Row className="header">
        <Col className="text-align-left title">
          <h2 className="title-primary">Mantenedor de Noches de Vacio</h2>
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col xs={24} sm={24} md={24} align="middle">
          <Card style={{ width: "100%" }} className="card_selector">
            <Col className="area">
              <label>Seleccione un area para empezar</label>
              <Select
                onChange={handleSelect}
                value={select}
                style={{ width: "160px", textAlign: "initial" }}
              >
                {typeSpecie.map((animal) => (
                  <Option key={animal.code} value={animal.code}>
                    {animal.name_specie}
                  </Option>
                ))}
              </Select>
            </Col>
          </Card>
        </Col>
      </Row>

      <Row
        gutter={[20, 20]}
        style={{
          marginTop: "20px",
        }}
      >
        <Col
          xs={24}
          sm={24}
          md={4}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
          }}
        >
          <PrimaryButton
            width={"100%"}
            onButtonClick={showDrawer}
            icon={<FieldTimeOutlined />}
          >
            Historial
          </PrimaryButton>

          <Card>
            <Menu
              onClick={onClick}
              style={{ width: "100%" }}
              mode="inline"
              selectedKeys={menuselect ? [String(menuselect)] : []}
            >
              <label>Transversales</label>

              {typeTransversal.map((transv) => {
                if (isFoodPlantSpecie) {
                  if (["4", "5", "6"].includes(transv.code)) {
                    return (
                      <Menu.Item key={transv.code}>{transv.name}</Menu.Item>
                    );
                  }
                  return null;
                }

                if (["1", "2", "3"].includes(transv.code)) {
                  return <Menu.Item key={transv.code}>{transv.name}</Menu.Item>;
                }

                return null;
              })}

              <label>Por perfil de usuario</label>

              {typePerson.map((person) => (
                <Menu.Item
                  key={buildProfileMenuKey(person)}
                  title={person.description}
                >
                  {titlePerson(person.name_type_personal)}
                </Menu.Item>
              ))}
            </Menu>
          </Card>
        </Col>

        <Col
          xs={24}
          sm={24}
          md={20}
          style={{
            minHeight: "100%",
          }}
        >
          {component}
        </Col>
      </Row>

      <HistorialDrawer
        onClose={onClose}
        type={"Noches"}
        specie={select}
        isVisible={isHistorialVisible}
      />
    </div>
  );
};

export default NochesVacioContainer;
